import { useCallback, useEffect, useMemo, useRef } from 'react'

import _ from 'lodash'

import { useCsatTrigger } from '@/features/csat'
import { useDigitalProductContext } from '@/shared/contexts/DigitalProductContext'
import { useRecord } from '@/shared/hooks'
import {
  describeUnplayableActivities,
  findStepIndexById,
  firstPlayableStepIndex,
  useJourneyAdapter,
  useLevelPlay
} from '@/shared/level-play'
import { api } from '@/shared/lib/api'
import { useJourneysStore } from '@/shared/store/useJourneysStore'
import type { Level } from '@/types/journey'

import { useTrackLevelAnalytics } from '../analytics'
import { getPlayability, getSteps } from './utils/getSteps'

import type { LevelContextType, EnrichedLevel } from './context'

/**
 * Wrapper de domínio da jornada sobre o motor compartilhado.
 *
 * Este hook **não** orquestra mais a execução — quem faz isso é
 * `useLevelPlay` (`shared/level-play`). O que sobra aqui é o que é de fato da
 * jornada:
 *
 * - carregar o nível e a jornada, e derivar os steps do bundle
 * - montar o `journeyAdapter`
 * - traduzir a superfície genérica do motor para o vocabulário da jornada
 *
 * **A tradução de nomes é o ponto central.** O contexto tem 33 consumidores que
 * usam `onChallengeComplete`, `onChallengeContinue`, `journeyAllowWrongAnswers`
 * e afins. Manter o mapeamento aqui é o que permite trocar o motor sem tocar em
 * nenhum deles — e adiar o merge das árvores de kinds para a Fase C.
 */
export const useLevel = ({
  id,
  config
}: {
  digitalProductId: string
  id: string
  config: any
}): Omit<LevelContextType, 'children'> => {
  const { digitalProduct, eventSlug } = useDigitalProductContext()
  const { triggerForLevel } = useCsatTrigger()
  const { apiBasePath } = config

  const { record: levelRaw, loadWrapperConfig } = useRecord({
    r: apiBasePath,
    id
  }) as {
    record: Level
    loadWrapperConfig: {
      isLoading: boolean
      isError: boolean
      isSuccess: boolean
    }
  }

  const journeyUid: string | undefined = levelRaw?.journey?.uid
  const { journey, getLevelDetails } = useJourneysStore({ uid: journeyUid })

  const level = useMemo<EnrichedLevel | null>(() => {
    if (!levelRaw) return null
    return getLevelDetails(levelRaw) as EnrichedLevel
  }, [levelRaw, getLevelDetails])

  const steps = useMemo(
    () => getSteps(level?._challenges || []),
    [level?._challenges]
  )

  /**
   * Contabiliza os desafios que a execução pulou.
   *
   * Antes eles sumiam sem deixar rastro, e o sintoma chegava distorcido: um
   * nível que não conclui, ou um "Continuar" sem efeito. O aviso vai para o
   * console — que o painel de debug captura e o relatório copiado inclui —, para
   * quem estiver diagnosticando ver quantos ficaram de fora e por quê.
   */
  const playability = useMemo(
    () => getPlayability(level?._challenges || []),
    [level?._challenges]
  )

  // `_challenges` é recriado a cada render pelo store, então o memo não segura
  // nada: sem esta trava o mesmo aviso sairia uma vez por render.
  const lastWarning = useRef<string | null>(null)

  useEffect(() => {
    const resumo = describeUnplayableActivities(playability.unplayable)

    if (!resumo || resumo === lastWarning.current) return

    lastWarning.current = resumo
    console.warn(resumo)
  }, [playability])

  const journeyAllowWrongAnswers = level?.journey?.allow_wrong_answers ?? false
  const journeyBlockRedo = _.get(level, 'journey.block_redo', false)

  const onFinish = useCallback(() => {
    if (level?.id) triggerForLevel(level.id)
  }, [level?.id, triggerForLevel])

  const adapter = useJourneyAdapter({
    eventSlug,
    steps,
    allowWrongAnswers: journeyAllowWrongAnswers,
    maxHearts: level?.max_attempts || 5,
    journeyUid
  })

  const engine = useLevelPlay({
    adapter,
    onFinish,
    resetKey: levelRaw?.id ?? null
  })

  useTrackLevelAnalytics({ level, currentScreen: engine.currentScreen })

  const getChallenge = useCallback(
    (challengeId: number | string) =>
      level?._challenges.find(challenge => challenge.id == challengeId),
    [level?._challenges]
  )

  /**
   * Retoma no último desafio não concluído.
   *
   * Quando o desafio pendente **não tem step**, pula para o primeiro step
   * jogável. Antes caía no índice 0 — a própria tela de abertura —, e o
   * participante clicava em "Continuar" para voltar exatamente para onde já
   * estava, sem erro no console e sem nenhuma pista. Acontecia sempre que o
   * pendente era de um kind sem componente (`hangman`, 18 níveis na demo) ou de
   * conteúdo vazio: `getSteps` os descarta, mas a API segue apontando para eles.
   *
   * Sem nenhum step jogável não há para onde ir, e aí a tela de abertura é o
   * destino correto — não há atividade nenhuma para apresentar.
   */
  const onChallengeStart = useCallback(() => {
    const pending = level?._lastUnfinishedChallenge

    if (!pending) {
      engine.goToFirstActivityStep()
      return
    }

    const index = findStepIndexById(steps, pending.id)

    if (index !== -1) {
      engine.goToStepIndex(index)
      return
    }

    const fallback = firstPlayableStepIndex(steps)

    engine.goToStepIndex(fallback !== -1 ? fallback : 0)
  }, [level?._lastUnfinishedChallenge, steps, engine])

  const onLevelReset = useCallback(async () => {
    await api.post(`${apiBasePath}/${id}/reset`)
    engine.goToFirstActivityStep()
  }, [apiBasePath, id, engine])

  const goToFinishScreen = useCallback(async () => {
    engine.goToFinishScreen()
  }, [engine])

  return {
    // --- dados da jornada, que o motor não conhece ---
    digitalProduct,
    levelId: id,
    level,
    journey,
    journeyUid,
    journeyAllowWrongAnswers,
    journeyBlockRedo,
    config,
    loadWrapperConfig,
    getChallenge,
    // Quantos desafios a execução de fato apresenta — a tela de abertura anuncia
    // este número, não o total do nível: prometer 2 desafios e entregar 1 é
    // desencontro que o participante percebe.
    playableChallengesCount: playability.playable.length,

    // --- comportamento próprio da jornada ---
    onChallengeStart,
    onLevelReset,
    goToFinishScreen,

    // --- motor, com os nomes que os consumidores da jornada esperam ---
    challengeCardRef: engine.activityCardRef,
    countdownRef: engine.countdownRef,
    hearts: engine.hearts,
    loseHeart: engine.loseHeart,
    onLooseHeart: engine.loseHeart,
    isSubmitting: engine.isSubmitting,
    setIsSubmitting: engine.setIsSubmitting,
    disableControls: engine.disableControls,
    disableInputs: engine.disableInputs,
    answerIsWrong: engine.answerIsWrong,
    answerIsRight: engine.answerIsRight,
    answerIsEmpty: engine.answerIsEmpty,
    methods: engine.methods,
    currentScreen: engine.currentScreen,
    currentStepIndex: engine.currentStepIndex,
    steps: engine.steps,
    totalSteps: engine.totalSteps,
    stepsCustomFooters: engine.stepsCustomFooters,
    setStepsCustomFooters: engine.setStepsCustomFooters,
    goToPrevStep: engine.goToPrevStep,
    goToNextStep: engine.goToNextStep,
    goToStepByChallengeId: engine.goToStepByActivityId,
    goToOutOfTimeScreen: engine.goToOutOfTimeScreen,
    goToOutOfHeartsScreen: engine.goToOutOfHeartsScreen,
    startCountdown: engine.startCountdown,
    pauseCountdown: engine.pauseCountdown,
    onAnswer: engine.onAnswer,
    onLevelRestart: engine.onRestart,
    onChallengeVerifyAnswer: engine.onVerifyAnswer,
    onChallengeComplete: engine.onComplete,
    onChallengeContinue: engine.onContinue,
    onChallengeAdvance: engine.onAdvance,
    onChallengeRetry: engine.onRetry
  } as Omit<LevelContextType, 'children'>
}
